import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Globe, LogOut, LibraryBig, Settings, Menu, X } from "lucide-react";

const Navbar = () => {
  const { user, login, logout } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const isReader = location.pathname.startsWith("/read/");
  if (isReader) return null;

  return (
    <header className="sticky top-0 z-50 w-full bg-[#F9F9F7]/85 backdrop-blur-xl border-b border-stone-200">
      <nav className="max-w-7xl mx-auto flex items-center justify-between px-6 md:px-12 h-16">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          <Globe className="w-5 h-5 text-[#9A3412] group-hover:rotate-12 transition-transform" />
          <span className="font-serif text-2xl font-semibold tracking-tight text-stone-900">UMoooV</span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          <Link to="/" data-testid="nav-guides" className="text-sm font-medium text-stone-600 hover:text-stone-900 transition-colors">Guides</Link>
          <Link to="/compare" data-testid="nav-compare" className="text-sm font-medium text-stone-600 hover:text-stone-900 transition-colors">Compare</Link>
          <a href="/#bundle" data-testid="nav-bundle" className="text-sm font-medium text-stone-600 hover:text-stone-900 transition-colors">Bundle</a>
          {user && (
            <Link to="/library" data-testid="nav-library" className="text-sm font-medium text-stone-600 hover:text-stone-900 transition-colors">My Library</Link>
          )}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="nav-user-menu" className="flex items-center gap-2 outline-none">
                  <Avatar className="w-9 h-9 border border-stone-300">
                    <AvatarImage src={user.picture} alt={user.name} />
                    <AvatarFallback className="bg-[#9A3412] text-white text-sm">{(user.name || user.email)[0]?.toUpperCase()}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2">
                  <p className="text-sm font-medium truncate">{user.name}</p>
                  <p className="text-xs text-stone-500 truncate">{user.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/library" data-testid="menu-library" className="cursor-pointer"><LibraryBig className="w-4 h-4 mr-2" />My Library</Link>
                </DropdownMenuItem>
                {user.is_admin && (
                  <DropdownMenuItem asChild>
                    <Link to="/admin" data-testid="menu-admin" className="cursor-pointer"><Settings className="w-4 h-4 mr-2" />Admin CMS</Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-logout" onClick={logout} className="cursor-pointer text-red-600"><LogOut className="w-4 h-4 mr-2" />Log out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button data-testid="nav-login-btn" onClick={login} className="bg-[#9A3412] hover:bg-[#7C2D12] text-white rounded-none px-6">Sign In</Button>
          )}
        </div>

        <button data-testid="nav-mobile-toggle" className="md:hidden" onClick={() => setOpen(!open)}>
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      {open && (
        <div className="md:hidden border-t border-stone-200 bg-[#F9F9F7] px-6 py-4 flex flex-col gap-4">
          <Link to="/" onClick={() => setOpen(false)} className="text-sm font-medium text-stone-700">Guides</Link>
          <Link to="/compare" onClick={() => setOpen(false)} className="text-sm font-medium text-stone-700">Compare</Link>
          {user && <Link to="/library" onClick={() => setOpen(false)} className="text-sm font-medium text-stone-700">My Library</Link>}
          {user?.is_admin && <Link to="/admin" onClick={() => setOpen(false)} className="text-sm font-medium text-stone-700">Admin CMS</Link>}
          {user ? (
            <Button onClick={logout} variant="outline" className="rounded-none">Log out</Button>
          ) : (
            <Button onClick={login} className="bg-[#9A3412] hover:bg-[#7C2D12] text-white rounded-none">Sign In</Button>
          )}
        </div>
      )}
    </header>
  );
};

export default Navbar;
